import { useTranslation } from 'react-i18next';

type Point = { subKey: string; bodyKey: string; img: string; altKey: string };

const points: Point[] = [
  {
    subKey: 'butlerSchool.definition.sub1',
    bodyKey: 'butlerSchool.definition.para1',
    img: '/images/generated/benefits-hero-2026.jpg',
    altKey: 'butlerSchool.definition.img1Alt',
  },
  {
    subKey: 'butlerSchool.definition.sub2',
    bodyKey: 'butlerSchool.definition.para2',
    img: '/images/generated/association-cert-2026.jpg',
    altKey: 'butlerSchool.definition.img2Alt',
  },
  {
    subKey: 'butlerSchool.definition.sub3',
    bodyKey: 'butlerSchool.definition.para3',
    img: '/images/generated/about-classroom-2026.jpg',
    altKey: 'butlerSchool.definition.img3Alt',
  },
];

function splitParagraphs(value: string): string[] {
  return value.split(/\n{2,}/).map((part) => part.trim()).filter((part) => part.length > 0);
}

export default function SchoolDefinition() {
  const { t } = useTranslation();

  return (
    <section className="relative py-24 md:py-32 bg-primary-900 overflow-hidden">
      <i
        aria-hidden="true"
        className="ri-double-quotes-l pointer-events-none absolute -top-8 left-2 md:left-10 text-accent-500/[0.06] text-[16rem] md:text-[22rem] leading-none"
      ></i>

      <div className="relative max-w-[1280px] mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
        <div className="max-w-[820px] mb-16 md:mb-20">
          <p className="text-accent-400 text-[13px] tracking-[0.26em] mb-6 font-label">
            {t('butlerSchool.definition.label')}
          </p>
          <h2 className="font-heading text-[28px] md:text-[40px] leading-[1.5] text-foreground-50 tracking-[0.06em]">
            {t('butlerSchool.definition.mainLine')}
          </h2>
          <div className="gold-line-left mt-8"></div>
        </div>

        <div className="space-y-14 md:space-y-20">
          {points.map((point, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div
                key={point.subKey}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center"
              >
                <div className={`lg:col-span-6 ${reverse ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div className="relative">
                    <div className="absolute -inset-2 border border-accent-500/20 pointer-events-none hidden md:block"></div>
                    <img
                      src={point.img}
                      alt={t(point.altKey)}
                      className="relative w-full aspect-[4/3] object-cover object-center"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className={`lg:col-span-6 ${reverse ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-8 h-px bg-accent-500/60"></span>
                    <span className="section-title-en text-accent-400/70 text-[13px] tracking-[0.16em]">
                      0{idx + 1}
                    </span>
                  </div>
                  <h3 className="font-heading text-[21px] md:text-[25px] text-foreground-50 tracking-[0.05em] leading-[1.6] mb-5">
                    {t(point.subKey)}
                  </h3>
                  {splitParagraphs(String(t(point.bodyKey))).map((para, paraIdx) => (
                    <p
                      key={paraIdx}
                      className="text-[16px] md:text-[17px] text-foreground-300 tracking-[0.03em] leading-[2.1]"
                    >
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 md:mt-20 max-w-[900px] mx-auto relative pl-6 md:pl-10 border-l-2 border-accent-500/50">
          <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-primary-900 border border-accent-500/40 flex items-center justify-center">
            <i className="ri-quote-text text-accent-400 text-xs"></i>
          </span>
          {splitParagraphs(String(t('butlerSchool.definition.example'))).map((para, paraIdx) => (
            <p
              key={paraIdx}
              className="font-heading text-[18px] md:text-[21px] text-foreground-100/90 tracking-[0.03em] leading-[2]"
            >
              {para}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}
